import {
  AIRTABLE_WAITLIST_FIELD_FORMATS,
  AIRTABLE_WAITLIST_FIELD_IDS,
  type AirtableWaitlistFieldKey,
  getAirtableWaitlistConfig,
} from '../config/airtableWaitlist';
import {
  displayBudgetRange,
  displayCompanySize,
  displayCompanyType,
  displayExpectedTrials,
  displayHeadquarters,
  displayHearAboutUs,
  displayRole,
  displayTimeframe,
} from '../utils/waitlistSelectDisplayValues';

const LOG_PREFIX = '[Lummo Waitlist → Airtable]';

function waitlistDebug(step: string, detail?: unknown): void {
  if (typeof console !== 'undefined' && console.info) {
    if (detail !== undefined) {
      console.info(LOG_PREFIX, step, detail);
    } else {
      console.info(LOG_PREFIX, step);
    }
  }
}

function summarizeFieldsForLog(
  fields: Record<string, string | string[]>
): Record<string, number> {
  const out: Record<string, number> = {};
  Object.entries(fields).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      out[key] = val.length;
    } else {
      out[key] = val.length;
    }
  });
  return out;
}

export interface WaitlistPayload {
  name: string;
  email: string;
  company: string;
  role: string;
  phone: string;
  companyType: string;
  companySize: string;
  headquarters: string;
  trialPhase: string[];
  therapeuticAreas: string[];
  targetCountries: string[];
  expectedTrials: string;
  timeframe: string;
  budgetRange: string;
  specificRequirements: string;
  hearAboutUs: string;
}

type AirtableCell = string | string[];

interface AirtableCreateResponse {
  records?: Array<{
    id?: string;
    fields?: Record<string, unknown>;
  }>;
  error?: string;
  message?: string;
}

function buildTrialNeedsText(data: WaitlistPayload): string {
  const lines: string[] = [];
  if (data.trialPhase.length > 0) {
    lines.push(`Trial phases: ${data.trialPhase.join(', ')}`);
  }
  if (data.therapeuticAreas.length > 0) {
    lines.push(`Therapeutic areas: ${data.therapeuticAreas.join(', ')}`);
  }
  if (data.targetCountries.length > 0) {
    lines.push(`Target countries: ${data.targetCountries.join(', ')}`);
  }
  if (data.expectedTrials.trim()) {
    lines.push(
      `Expected trials per year: ${displayExpectedTrials(data.expectedTrials)}`
    );
  }
  if (data.timeframe.trim()) {
    lines.push(`Timeframe to start: ${displayTimeframe(data.timeframe)}`);
  }
  return lines.join('\n');
}

function buildFields(data: WaitlistPayload): Record<string, AirtableCell> {
  const f = AIRTABLE_WAITLIST_FIELD_IDS;
  const fmt = AIRTABLE_WAITLIST_FIELD_FORMATS;

  const rawValues: Record<AirtableWaitlistFieldKey, string | string[]> = {
    name: data.name.trim(),
    email: data.email.trim(),
    company: data.company.trim(),
    role: displayRole(data.role),
    phone: data.phone.trim(),
    companyType: displayCompanyType(data.companyType),
    companySize: displayCompanySize(data.companySize),
    headquarters: displayHeadquarters(data.headquarters),
    trialNeeds: buildTrialNeedsText(data),
    trialPhases: [...data.trialPhase],
    therapeuticAreas: [...data.therapeuticAreas],
    targetCountries: [...data.targetCountries],
    expectedTrials: displayExpectedTrials(data.expectedTrials),
    timeframe: displayTimeframe(data.timeframe),
    hearAboutUs: displayHearAboutUs(data.hearAboutUs),
    budgetRange: displayBudgetRange(data.budgetRange),
    specificRequirements: data.specificRequirements.trim(),
  };

  const out: Record<string, AirtableCell> = {};

  (Object.keys(f) as AirtableWaitlistFieldKey[]).forEach((key) => {
    const columnName = f[key];
    const value = rawValues[key];
    const format = fmt[key];

    if (format === 'multi_select') {
      const arr = value as string[];
      if (arr.length > 0) {
        out[columnName] = [...arr];
      }
      return;
    }

    if (format === 'long_text') {
      const str = typeof value === 'string' ? value.trim() : '';
      if (str !== '') {
        out[columnName] = str;
      }
      return;
    }

    const str = typeof value === 'string' ? value.trim() : '';
    if (str !== '') {
      out[columnName] = str;
    }
  });

  return out;
}

export async function submitWaitlistToAirtable(
  data: WaitlistPayload
): Promise<void> {
  waitlistDebug('submit started');

  const cfg = getAirtableWaitlistConfig();
  if (!cfg) {
    waitlistDebug('config missing', {
      hasToken: Boolean(process.env.REACT_APP_AIRTABLE_TOKEN?.trim()),
      hasBaseId: Boolean(process.env.REACT_APP_AIRTABLE_BASE_ID?.trim()),
    });
    throw new Error(
      'Missing Airtable configuration. Set REACT_APP_AIRTABLE_TOKEN and REACT_APP_AIRTABLE_BASE_ID (and optionally REACT_APP_AIRTABLE_TABLE_ID) in your environment before building.'
    );
  }

  waitlistDebug('config OK', {
    baseId: cfg.baseId,
    tableId: cfg.tableId,
    tokenChars: cfg.token.length,
  });

  const fields = buildFields(data);
  const fieldKeys = Object.keys(fields);

  waitlistDebug('payload field names (Airtable columns)', fieldKeys);
  waitlistDebug('payload value sizes (chars or array length)', summarizeFieldsForLog(fields));

  if (process.env.NODE_ENV === 'development') {
    waitlistDebug('payload fields (full — dev only)', fields);
  }

  if (fieldKeys.length === 0) {
    waitlistDebug('abort: no fields to send after buildFields()');
    throw new Error(
      'Nothing to send to Airtable — check that your form is complete and that field names in src/config/airtableWaitlist.ts match your base.'
    );
  }

  const url = `https://api.airtable.com/v0/${encodeURIComponent(cfg.baseId)}/${encodeURIComponent(cfg.tableId)}`;
  const body = {
    records: [
      {
        fields,
      },
    ],
  };

  waitlistDebug('POST', url);

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${cfg.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    waitlistDebug('fetch threw (network / CORS / blocked)', err);
    throw err instanceof Error
      ? err
      : new Error('Network error while contacting Airtable.');
  }

  waitlistDebug('response status', res.status);

  let payload: AirtableCreateResponse;
  try {
    payload = (await res.json()) as AirtableCreateResponse;
  } catch (err) {
    waitlistDebug('response JSON parse failed', err);
    throw new Error(`Airtable response was not JSON (HTTP ${res.status}).`);
  }

  if (!res.ok) {
    const detail =
      typeof payload === 'object' && payload !== null
        ? JSON.stringify(payload)
        : String(payload);
    waitlistDebug('error response body', detail);
    throw new Error(
      `Airtable error (${res.status}): ${detail || res.statusText}`
    );
  }

  waitlistDebug('success response body (summary)', {
    recordCount: payload.records?.length ?? 0,
    firstId: payload.records?.[0]?.id,
    firstFieldKeys: payload.records?.[0]?.fields
      ? Object.keys(payload.records[0].fields as object)
      : [],
  });

  if (process.env.NODE_ENV === 'development' && payload.records?.[0]) {
    waitlistDebug('success response body (full first record — dev only)', payload.records[0]);
  }

  const created = payload.records?.[0];
  if (!created?.id) {
    waitlistDebug('abort: no record id in payload', payload);
    throw new Error(
      `Unexpected Airtable response (no record id): ${JSON.stringify(payload)}`
    );
  }

  const returnedKeys = created.fields ? Object.keys(created.fields) : [];
  if (returnedKeys.length === 0) {
    waitlistDebug('abort: created record but fields object empty', {
      id: created.id,
    });
    throw new Error(
      'Airtable created a row but no fields were saved. Usually every key in the request was an unknown field name: open your table in Airtable, copy each column name exactly (including spaces and accents), and update AIRTABLE_WAITLIST_FIELD_IDS in src/config/airtableWaitlist.ts so they match.'
    );
  }

  waitlistDebug('submit OK', { recordId: created.id, returnedFieldCount: returnedKeys.length });
}
